import { useEffect, useState } from "react";
import Modal from "../../../componentes/modal/modal";
import Button from "../../../componentes/button/button";
import FleetcomService from "../../../services/resource/fleetcomService";

const fleetcomService = new FleetcomService();

/**
 * Detalhe de um veículo a partir da placa, sempre consultado ao vivo no Fleetcom — a Solicitação
 * de Compras não guarda mais nenhum dado do veículo além da placa (ver ProdutoDTO.placaVeiculo),
 * então esta é a única fonte dos demais atributos. `placa` controla a busca (muda -> refaz);
 * `isOpen` só controla a visibilidade do modal.
 */
export default function VeiculoFleetcomModal({ isOpen, placa, onClose }) {
    const [carregando, setCarregando] = useState(false);
    const [erro, setErro] = useState(false);
    const [veiculo, setVeiculo] = useState(null);

    useEffect(() => {
        if (!isOpen || !placa) return undefined;

        setCarregando(true);
        setErro(false);
        setVeiculo(null);

        let cancelado = false;
        fleetcomService.buscarVeiculoPorPlaca(placa)
            .then(res => {
                if (cancelado) return;
                const lista = res.data || [];
                setVeiculo(lista.length > 0 ? lista[0] : null);
            })
            .catch(() => {
                if (!cancelado) setErro(true);
            })
            .finally(() => {
                if (!cancelado) setCarregando(false);
            });

        return () => { cancelado = true; };
    }, [isOpen, placa]);

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Veículo"
            subtitle={placa ? `Placa ${placa} — dados consultados ao vivo na Fleetcom` : null}
            className="w-[95%] max-w-2xl"
            footer={
                <div className="flex justify-end">
                    <Button variant="outline" label="Fechar" onClick={onClose} />
                </div>
            }
        >
            <div className="p-4 min-h-[10rem]">
                {carregando && (
                    <div className="flex items-center justify-center py-10 text-sm text-muted-foreground">
                        <i className="fa fa-spinner fa-spin mr-2" /> Buscando dados na Fleetcom...
                    </div>
                )}

                {!carregando && erro && (
                    <div className="flex flex-col items-center gap-2 py-10 text-center">
                        <i className="fa fa-exclamation-triangle text-destructive text-xl" />
                        <p className="text-sm text-foreground">Erro ao consultar o veículo na Fleetcom.</p>
                        <p className="text-xs text-muted-foreground">Tente novamente em instantes.</p>
                    </div>
                )}

                {!carregando && !erro && !veiculo && (
                    <div className="flex flex-col items-center gap-2 py-10 text-center">
                        <i className="fa fa-car-side text-muted-foreground text-xl" />
                        <p className="text-sm text-foreground">Veículo não encontrado na Fleetcom.</p>
                        <p className="text-xs text-muted-foreground">A placa {placa} não retornou resultados.</p>
                    </div>
                )}

                {!carregando && !erro && veiculo && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
                        <Campo label="Placa" valor={veiculo.placa} />
                        <Campo label="Código" valor={veiculo.codigo} />
                        <Campo label="Modelo" valor={veiculo.modelo} />
                        <Campo label="Fabricante" valor={veiculo.fabricante} />
                        <Campo label="Tipo" valor={veiculo.tipo} />
                        <Campo label="Centro de Custo" valor={veiculo.centroCusto} />
                        <Campo label="Filial" valor={veiculo.filial} />
                        <Campo label="Ano Fabricação" valor={veiculo.anoFabricacao} />
                        <Campo label="Ano Modelo" valor={veiculo.anoModelo} />
                        <Campo label="KM Rodado" valor={veiculo.kmRodado != null ? Number(veiculo.kmRodado).toLocaleString("pt-BR") : null} />
                        <Campo label="UF" valor={veiculo.estado} />
                        <Campo label="Chassis" valor={veiculo.chassis} />
                        <Campo label="Renavam" valor={veiculo.renavam} />
                    </div>
                )}
            </div>
        </Modal>
    );
}

// Some campo a campo quando o Fleetcom não retorna o atributo — evita mostrar "—" para tudo que
// falta (dado incompleto é esperado, não é um estado de erro).
function Campo({ label, valor }) {
    if (valor === null || valor === undefined || valor === "") return null;
    return (
        <div className="min-w-0">
            <span className="block text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">{label}</span>
            <span className="block text-foreground font-medium truncate" title={String(valor)}>{valor}</span>
        </div>
    );
}
